import React, { useEffect } from 'react';
import InnerBlock from '../../../shared/web/layout/InnerBlock';
import { useSearchParams } from 'react-router-dom';
import { LoadingAnimation, usePopupManager } from 'ui-builder';
import { BadRequestError, useFubConnectionProfilesService } from 'nekst-api';
import useSettingsNavigator from '../../settingsNavigator';
import { FubIntegrationToggleWrapper } from 'feature-toggle';
import { Popup, PopupText } from 'features/nekst-widgets';

function useRedirectToConnectionPage() {
  const navigator = useSettingsNavigator();

  useEffect(() => {
    return () => {
      navigator.openFubProfilesPage();
    }
  }, []);
}

function AlreadyLinkedPopup() {
  useRedirectToConnectionPage();

  return (
    <Popup
      title="FUB Account Already Linked"
      content={(
        <PopupText>
          This Follow Up Boss account is already linked to another Nekst team. Please reach out to support for assistance.
        </PopupText>
      )}
    />
  );
}

function UnknownErrorPopup() {
  useRedirectToConnectionPage();

  return (
    <Popup
      title="Unknown Error"
      content={(
        <PopupText>
          An unexpected error occurred while linking the Follow Up Boss account. Please contact support for assistance.
        </PopupText>
      )}
    />
  );
}

enum Error {
  ALREADY_LINKED = 'ALREADY_LINKED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export default function FubOauthCode() {
  const [params] = useSearchParams();

  const code = params.get('code');
  const state = params.get('state');

  const fubProfilesService = useFubConnectionProfilesService();
  const settingsNavigator = useSettingsNavigator();

  const [error, setError] = React.useState<Error | null>(null);

  const popupManager = usePopupManager();

  useEffect(() => {
    if (error) {
      if (error === Error.ALREADY_LINKED) {
        popupManager.openPopup(<AlreadyLinkedPopup />);
      } else if (error === Error.UNKNOWN_ERROR) {
        popupManager.openPopup(<UnknownErrorPopup />);
      }
    }
  }, [error]);

  async function createProfile() {
    try {
      await fubProfilesService.createProfile(
        code as string,
        state as string,
      );

      settingsNavigator.openFubProfilesPage();
    } catch (e) {
      if (e instanceof BadRequestError) {
        if (e.getErrorCode() === Error.ALREADY_LINKED) {
          setError(Error.ALREADY_LINKED);
        } else {
          setError(Error.UNKNOWN_ERROR);
        }
      } else {
        setError(Error.UNKNOWN_ERROR);
      }
    }
  }

  useEffect(() => {
    createProfile();
  }, []);

  return (
    <FubIntegrationToggleWrapper>
      <InnerBlock title="Follow Up Boss Integration">
        <LoadingAnimation />
      </InnerBlock>
    </FubIntegrationToggleWrapper>
  );
}
